import { useCallback, useMemo, useState } from "react";
import { Box } from "@mui/material";
import { Theme } from "@analyticsMD/quil";
import { ErrorBoundary } from "react-error-boundary";
import { useSearchParams } from "react-router-dom";

import ErrorPage from "../components/error-page";
import EmbededAppContainer from "../components/embedded-container";
import StaticFilterBar from "../components/filter/static-filter-bar";
import DepartmentSelector from "../components/list/department-selector";
import ListPageShell from "../components/list/list-page-shell";
import { useFilteredRows } from "../components/list/use-filtered-rows";
import { useListDrawer } from "../components/list/use-list-drawer";
import {
  buildViewOptions,
  findViewOption,
  viewQueryParams,
} from "../components/list/view-options";
import MalnutritionDrawer from "../components/malnutrition/malnutrition-drawer";
import { getMalnutritionListColumns } from "../columns/malnutritionlist.columns";
import { useMalnutritionListQuery } from "../api/malnutrition/queries";
import {
  malnutritionUseMocks,
  MOCK_MALNUTRITION_DEPARTMENT_GROUPS,
  MOCK_MALNUTRITION_DEPARTMENTS,
} from "../api/malnutrition/mocks";
import {
  useConfigurationQuery,
  useDepartmentGroupsQuery,
  useDepartmentsQuery,
} from "../api/queries";

import type { FC } from "react";
import type { GridSortModel } from "@mui/x-data-grid";
import type { StaticFilter } from "../components/filter/static-filter-bar";
import type { SearchBarProps } from "../components/filter/search-bar";
import type { ListView, ListViewOption } from "../components/list/view-options";
import type MalnutritionListEntry from "../types/MalnutritionListEntry";

const DEFAULT_SORT: GridSortModel = [{ field: "status", sort: "asc" }];
const PAGE_SIZES = [20, 50, 100];
const SEARCH: SearchBarProps = {
  variant: "outlined",
  placeholder: "Search patient, bed, ID",
};
const GRID_SX = { "& .MuiDataGrid-cell": { alignItems: "center" } };

// A link names its board as ?group= or ?department=&qv_hospital_id=.
const viewFromSearch = (search: URLSearchParams): ListView => {
  const group = search.get("group");
  if (group) return { kind: "group", id: group, hospitalID: null };
  const department = search.get("department");
  if (!department) return { id: null, hospitalID: null };
  const hospitalID = Number(search.get("qv_hospital_id"));
  return { kind: "department", id: department, hospitalID: hospitalID || null };
};

const distinct = (values: string[]): string[] =>
  Array.from(new Set(values.filter(Boolean))).sort((a, b) =>
    a.localeCompare(b)
  );

const buildFilters = (rows: MalnutritionListEntry[]): StaticFilter[] => [
  {
    id: 1,
    field: "current_department",
    label: "Department",
    filter_type: "multi",
    filter_options: distinct(rows.map((row) => row.current_department)),
    width: "14rem",
  },
  {
    id: 2,
    field: "criteria_met",
    label: "Clinical indicators",
    filter_type: "multi",
    filter_options: distinct(
      rows.flatMap((row) =>
        row.criteria_met.map((criterion) => criterion.title)
      )
    ),
    width: "15.5rem",
  },
];

const Layout: FC = () => {
  const useMocks = malnutritionUseMocks();
  // CGCA's boards: the QList's, CGCA-only ones, and any unconfigured unit
  // holding an eligible patient. Mock mode has no backend to list boards, so
  // it brings its own.
  const departmentsQuery = useDepartmentsQuery("CGCA", !useMocks);
  const departmentGroupsQuery = useDepartmentGroupsQuery("CGCA", !useMocks);
  const viewOptions = useMemo(
    () =>
      useMocks
        ? buildViewOptions(
            MOCK_MALNUTRITION_DEPARTMENTS,
            MOCK_MALNUTRITION_DEPARTMENT_GROUPS
          )
        : buildViewOptions(
            departmentsQuery.data || [],
            departmentGroupsQuery.data || []
          ),
    [useMocks, departmentsQuery.data, departmentGroupsQuery.data]
  );

  // The board is kept in the URL so a link opens straight onto it. The open
  // patient never is.
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedView, setSelectedView] = useState(() =>
    viewFromSearch(searchParams)
  );
  const [filterValues, setFilterValues] = useState<Record<number, string[]>>(
    {}
  );

  const drawer = useListDrawer<MalnutritionListEntry>();
  const closeDrawer = drawer.close;

  const selectView = useCallback(
    (option: ListViewOption) => {
      setSelectedView({
        kind: option.kind,
        id: option.id,
        hospitalID: option.hospitalID,
      });
      setFilterValues({});
      closeDrawer();
      const { department, group, hospitalID } = viewQueryParams(option);
      setSearchParams(
        (prev) => {
          const next = new URLSearchParams(prev);
          ["department", "qv_hospital_id", "group"].forEach((key) =>
            next.delete(key)
          );
          if (group) {
            next.set("group", group);
          } else {
            next.set("department", department);
            if (hospitalID) next.set("qv_hospital_id", String(hospitalID));
          }
          return next;
        },
        { replace: true }
      );
    },
    [setSearchParams, closeDrawer]
  );

  const listParams = viewQueryParams(selectedView);
  const listQuery = useMalnutritionListQuery(
    listParams.department,
    listParams.group,
    listParams.hospitalID
  );
  // The configuration picks the identifier column's field, so rows wait for
  // it, as the QList's do. Mock mode has no backend to read it from.
  const configurationQuery = useConfigurationQuery(!useMocks);
  const configuration = configurationQuery.data?.[0] ?? null;
  const rows = useMemo(
    () => (configurationQuery.isLoading ? [] : listQuery.data || []),
    [configurationQuery.isLoading, listQuery.data]
  );
  const columns = useMemo(
    () => getMalnutritionListColumns(configuration),
    [configuration]
  );
  const filters = useMemo(() => buildFilters(rows), [rows]);
  const activeFilters = useMemo(
    () =>
      filters
        .filter((filter) => filterValues[filter.id]?.length > 0)
        .map((filter) => ({ ...filter, value: filterValues[filter.id] })),
    [filters, filterValues]
  );
  const filteredRows = useFilteredRows(rows, columns, activeFilters);

  const getRowHeight = useCallback(() => "auto" as const, []);
  const getEstimatedRowHeight = useCallback(() => 60, []);

  const loadError = [
    listQuery,
    configurationQuery,
    departmentsQuery,
    departmentGroupsQuery,
  ].find((query) => query.isError && !query.data)?.error;
  if (loadError) {
    return <ErrorPage error={loadError} />;
  }

  return (
    <Theme>
      <Box>
        <EmbededAppContainer>
          <ListPageShell
            viewSelector={
              <DepartmentSelector
                options={viewOptions}
                value={findViewOption(viewOptions, selectedView)}
                setSelectedDept={selectView}
                // The board lives in the URL here, not in a user preference.
                handleUpdateDefaultDepartment={() => undefined}
              />
            }
            search={SEARCH}
            filters={
              <StaticFilterBar
                filters={filters}
                values={filterValues}
                onChange={(filterID, values) =>
                  setFilterValues((prev) => ({ ...prev, [filterID]: values }))
                }
              />
            }
            noRowsMessage={
              selectedView.id
                ? "No flagged patients in this view"
                : "Choose a view to see flagged patients"
            }
            gridProps={{
              rows: filteredRows,
              columns,
              loading: listQuery.isLoading || configurationQuery.isLoading,
              getRowHeight,
              getEstimatedRowHeight,
              pageSizeOptions: PAGE_SIZES,
              initialState: {
                sorting: { sortModel: DEFAULT_SORT },
                pagination: { paginationModel: { pageSize: PAGE_SIZES[0] } },
              },
              onRowClick: (params) => drawer.open(params.row),
              onCellKeyDown: (params, event) => {
                if (event.key === "Enter") drawer.open(params.row);
              },
              rowSelectionModel: drawer.rowSelectionModel,
              onRowSelectionModelChange: drawer.setRowSelectionModel,
            }}
            gridSx={GRID_SX}
            drawer={{
              isOpen: drawer.isOpen,
              rowId: drawer.selectedRow?.id ?? null,
              onClose: drawer.close,
              renderContent: (closeButton) => (
                <MalnutritionDrawer
                  patient={drawer.selectedRow}
                  closeButtonCmpt={closeButton}
                />
              ),
            }}
          />
        </EmbededAppContainer>
      </Box>
    </Theme>
  );
};

// Flagged patients on a board, each opening the malnutrition card. Standalone
// from a link: no ListAPIContext, so no rounding, dispositions or preferences.
const MalnutritionListPage: FC = () => (
  <ErrorBoundary FallbackComponent={ErrorPage}>
    <Layout />
  </ErrorBoundary>
);

export default MalnutritionListPage;
